import React, { useState } from 'react';
import NavigationVariantIcon from 'mdi-react/NavigationVariantIcon';
import CrosshairsGpsIcon from 'mdi-react/CrosshairsGpsIcon';
import { YelpWordmark } from './yelp';
import { LOCATION_SOURCE } from './useGcmLocation';
import './GcmHeader.scss';

const GCM_LOGO = `${process.env.PUBLIC_URL}/img/gcm/gift-card-market-logo.png`;

// Gift Card Market logo (attribution for the Yelp content). Centered above the search by default;
// `gcm-brand--inline` sits it at the right of a heading row (landing, product, cart, checkout, gift codes).
export const GcmBrand = ({ className = '' }) => (
    <div className={`gcm-brand ${className}`}>
        <img className="gcm-brand__gcm" src={GCM_LOGO} alt="Gift Card Market" />
    </div>
);

// Inline replacement for window.prompt(): the city becomes a small form in place
const LocationEditor = ({ location, onSave, onCancel, onUseDevice, locating }) => {
    const [draft, setDraft] = useState(location);

    const submit = (e) => {
        e.preventDefault();
        const value = draft.trim();
        if (value && value !== location) onSave(value);
        else onCancel();
    };

    return (
        <form className="gcm-location__form" onSubmit={submit}>
            <input
                className="gcm-location__input"
                type="text"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && onCancel()}
                placeholder='City, ST (e.g. "New York, NY")'
                aria-label="Your location"
                autoFocus
            />
            <button type="submit" className="gcm-location__save">Update</button>
            <button type="button" className="gcm-location__cancel" onClick={onCancel}>Cancel</button>
            {onUseDevice && (
                <button type="button" className="gcm-location__device" onClick={onUseDevice} disabled={locating}>
                    <CrosshairsGpsIcon size={14} />
                    {locating ? 'Locating…' : 'Use my current location'}
                </button>
            )}
        </form>
    );
};

// The sentence under the search bar, worded by how the location was chosen.
// Coordinates from the device aren't shown; they read as "your current location".
const LocationMessage = ({ location, source, onChange }) => {
    const change = (text) => (
        <button type="button" className="gcm-location__city" onClick={onChange} title="Change location">
            {text}
        </button>
    );

    switch (source) {
        case LOCATION_SOURCE.DEVICE:
            return <>Showing merchants near your current location. {change('Change')}</>;
        case LOCATION_SOURCE.MANUAL:
            return <>Showing merchants near {change(location)}.</>;
        case LOCATION_SOURCE.DEFAULT:
            return <>We couldn't detect your location, so we're showing {change(location)}. Not correct? Specify your location above!</>;
        default:
            return <>It looks like you're in {change(location)}. Not correct? Specify your location above!</>;
    }
};

// Merchant search with Apply/Reset, plus the location line underneath.
// `geo` is the useGcmLocation() result for the page.
// `brand` adds the Gift Card Market logo ahead of "Powered by Yelp"
export const GcmSearchBar = ({ value, onChange, onApply, onReset, geo, brand = false }) => {
    const [editingLocation, setEditingLocation] = useState(false);
    const { location, source, setLocation, locateDevice, locating, error } = geo;

    const saveLocation = (next) => {
        setEditingLocation(false);
        setLocation(next);
    };

    const useDevice = async () => {
        if (await locateDevice()) setEditingLocation(false);
    };

    return (
        <>
            <div className="gcm-search">
                <input
                    className="gcm-search__input"
                    type="text"
                    placeholder="Search local merchants by name"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onApply()}
                />
                <button type="button" className="gcm-search__btn gcm-search__btn--apply" onClick={onApply}>
                    Apply Filter
                </button>
                <button type="button" className="gcm-search__btn gcm-search__btn--reset" onClick={onReset}>
                    Reset
                </button>
            </div>
            <div className="gcm-location">
                <span className="gcm-location__text">
                    <NavigationVariantIcon size={18} className="gcm-location__icon" />
                    {!location ? 'Detecting your location…' : editingLocation ? (
                        <LocationEditor
                            location={source === LOCATION_SOURCE.DEVICE ? '' : location}
                            onSave={saveLocation}
                            onCancel={() => setEditingLocation(false)}
                            onUseDevice={locateDevice && useDevice}
                            locating={locating}
                        />
                    ) : (
                        <LocationMessage location={location} source={source} onChange={() => setEditingLocation(true)} />
                    )}
                    {error && <span className="gcm-location__error" role="alert">{error}</span>}
                </span>
                <span className="gcm-location__yelp">
                    {brand && <GcmBrand className="gcm-brand--beside-yelp" />}
                    Powered By&nbsp;<YelpWordmark />
                </span>
            </div>
        </>
    );
};
